export type NormalizableButton = {
  href?: string;
  onClick?: (...args: any[]) => void;
  as?: 'a' | 'button';
};

export type NormalizedButton<T extends NormalizableButton> = Omit<
  T,
  'href' | 'onClick' | 'as'
> & {
  href?: string;
  onClick?: T['onClick'];
  as?: 'a' | 'button';
};

export const normalizeButtons = <T extends NormalizableButton>(
  buttons?: T[],
): NormalizedButton<T>[] | undefined => {
  if (!buttons || buttons.length === 0) {
    return buttons;
  }

  return buttons.map(button => {
    const href = button?.href ?? undefined;

    if (href) {
      const { onClick: _onClick, ...rest } = button;
      return {
        ...rest,
        href,
        as: 'a',
      } as NormalizedButton<T>;
    }

    return {
      ...button,
      as: 'button',
    } as NormalizedButton<T>;
  });
};

export const normalizeLinkButtons = <T extends NormalizableButton>(
  buttons?: T[],
): Array<NormalizedButton<T> & { href: string }> | undefined => {
  const normalized = normalizeButtons(buttons);
  if (!normalized || normalized.length === 0) {
    return undefined;
  }

  const linkButtons = normalized.filter(
    (button): button is NormalizedButton<T> & { href: string } =>
      Boolean(button.href),
  );

  return linkButtons.length > 0 ? linkButtons : undefined;
};
